import { type PresetView } from "~/components/planning/presetView"
import { type SavedTableView } from "~/server/api/routers/tableView"

/**
 * A view referenced from outside the component tree — a URL `?view=` param or a stored default.
 * A custom saved view is referenced by its DB id; a Hakio-provided preset by its string preset id.
 * `null` means "nothing selected", which callers read as "fall back to the system default".
 */
export type ViewTarget = { viewId: number } | { presetId: string } | null

const PRESET_PREFIX = "preset:"

/**
 * `preset:<id>` → a Hakio preset; digits (as a string, or a number — the router pre-parses
 * `?view=42` into one) → a saved view id. Anything else yields `null`, so a malformed link
 * degrades to the default view rather than erroring.
 */
export function parseViewParam(raw: string | number | undefined): ViewTarget {
  if (raw === undefined || raw === "") return null

  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw >= 0 ? { viewId: raw } : null
  }

  if (raw.startsWith(PRESET_PREFIX)) {
    const presetId = raw.slice(PRESET_PREFIX.length)
    return presetId ? { presetId } : null
  }

  // Deliberately strict: Number() would accept "1e3", " 12" and "0x1f" as view ids.
  if (!/^\d+$/.test(raw)) return null
  return { viewId: Number(raw) }
}

/**
 * Encode a target back into a `?view=` value. Returns undefined for `null`, which drops the param.
 * A view id stays numeric — the router JSON-encodes numeric-looking strings, giving `?view=%225%22`.
 */
export function encodeViewParam(target: ViewTarget): string | number | undefined {
  if (!target) return undefined
  return "viewId" in target ? target.viewId : `${PRESET_PREFIX}${target.presetId}`
}

/**
 * Shareable absolute URL for `href` with `?view=` pointing at `target`. Values are written raw,
 * matching what the router's stringifier emits for the same target, so the link parses back through
 * `parseViewParam` unchanged. A null target isn't linkable: without a `view` param the recipient
 * lands on their own default view, not on the system default.
 */
export function buildViewLink(target: NonNullable<ViewTarget>, href: string): string {
  const url = new URL(href)
  url.searchParams.set("view", String(encodeViewParam(target)))
  return url.toString()
}

/** Whether a target names this saved view. False for a preset target, an absent one, or no id. */
export function targetNamesView(target: ViewTarget, viewId: number | undefined): boolean {
  return !!target && "viewId" in target && viewId !== undefined && target.viewId === viewId
}

export type ResolvedViewTarget =
  | { kind: "view"; view: SavedTableView }
  | { kind: "preset"; preset: PresetView }

/**
 * Resolve a target against the views/presets currently available to the user.
 *
 * Returns `null` when the target is absent or unavailable (view deleted or access lost, preset gone
 * or disabled) — the caller then loads the system default. `finalViews` is already server-scoped to
 * this user and page, so an id the viewer may not see simply isn't found and falls back.
 */
export function resolveViewTarget({
  target,
  finalViews,
  presets,
}: {
  target: ViewTarget
  finalViews: SavedTableView[]
  presets: PresetView[]
}): ResolvedViewTarget | null {
  if (!target) return null

  if ("viewId" in target) {
    const view = finalViews.find((v) => v.id === target.viewId)
    return view ? { kind: "view", view } : null
  }

  const preset = presets.find((p) => p.id === target.presetId)
  // A disabled preset is not selectable, so it can't be linked to or act as a default either.
  return preset && !preset.disabled ? { kind: "preset", preset } : null
}
